import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { MapZoomableFrame } from '../components/MapZoomableFrame';
import { TechServicesMapDistrictModal } from '../components/TechServicesMapDistrictModal';
import { addProvinceLabels, addDistrictLabels } from '../utils/turkeyMapProvinceLabels';
import {
  fetchAdm2Markup,
  createDistrictSliceSvg,
  fitDistrictSliceViewBox,
  platePrefixFromIso2,
} from '../utils/turkeyMapAdm2Slice';
import { svgDistrictGroupToDbDistrictId } from '../utils/turkeyMapDistrictDbMatch';
import { fetchDistrictsByCity } from '../services/locationsService';
import { fetchServiceAreasByCityPlate } from '../services/serviceAreasService';
import { fetchCategories } from '../services/categoriesService';
import { fetchMapLabelState, EMPTY_MAP_LABEL_STATE } from '../services/mapLabelsService';
import './TechServicesMapPage.css';

const normalizeProductCategories = (response) => {
  const list = response?.veri?.kategoriler || response?.veri || [];
  return Array.isArray(list) ? list : [];
};

const MAP_SRC = `${process.env.PUBLIC_URL || ''}/maps/tr-adm1.svg`;

const DISTRICT_AUTHORIZED_CLASS = 'tech-services-map-page__district--authorized';

const TechServicesMapPage = () => {
  const countryMountRef = useRef(null);
  const districtMountRef = useRef(null);

  const [viewMode, setViewMode] = useState('country');
  const [activeProvince, setActiveProvince] = useState(null);

  const [countryLoading, setCountryLoading] = useState(true);
  const [countryError, setCountryError] = useState('');
  const [districtLoading, setDistrictLoading] = useState(false);
  const [districtError, setDistrictError] = useState('');
  const [cityDistricts, setCityDistricts] = useState([]);
  /** @type {number[]} yetkili servisi olan ilçe id'leri (DB districts.id), ilçe haritasında boyama için */
  const [cityAuthorizedDistrictIds, setCityAuthorizedDistrictIds] = useState([]);
  const [districtModal, setDistrictModal] = useState(null);
  const [mapProductCategories, setMapProductCategories] = useState([]);
  const [mapLabelState, setMapLabelState] = useState(EMPTY_MAP_LABEL_STATE);
  const [mapLabelStateReady, setMapLabelStateReady] = useState(false);

  const mainProductCategories = useMemo(
    () =>
      mapProductCategories.filter((c) => !c.ustKategoriId),
    [mapProductCategories]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchCategories({ kategoriTipi: 'urun' });
        if (!cancelled) setMapProductCategories(normalizeProductCategories(res));
      } catch {
        if (!cancelled) setMapProductCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const state = await fetchMapLabelState();
        if (!cancelled) setMapLabelState(state);
      } catch {
        if (!cancelled) setMapLabelState(EMPTY_MAP_LABEL_STATE);
      } finally {
        if (!cancelled) setMapLabelStateReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeCityPlate = useMemo(() => {
    const p = activeProvince?.iso2 ? platePrefixFromIso2(activeProvince.iso2) : null;
    if (!p) return null;
    const n = parseInt(p, 10);
    return Number.isFinite(n) && n >= 1 && n <= 81 ? n : null;
  }, [activeProvince?.iso2]);

  // ——— Türkiye (adm1) ———
  useEffect(() => {
    if (viewMode !== 'country') return undefined;

    const el = countryMountRef.current;
    if (!el) return undefined;

    let cancelled = false;

    const load = async () => {
      setCountryLoading(true);
      setCountryError('');
      try {
        const res = await fetch(MAP_SRC);
        if (!res.ok) throw new Error(`Harita yüklenemedi (${res.status})`);
        const text = await res.text();
        if (cancelled) return;
        el.innerHTML = text;
        const svg = el.querySelector('svg');
        if (svg) {
          svg.removeAttribute('style');
          svg.style.background = 'transparent';
          svg.style.display = 'block';
          svg.style.width = '100%';
          svg.style.height = 'auto';
          svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          svg.setAttribute('role', 'img');
          svg.setAttribute('aria-label', 'Türkiye illeri haritası');
        }
      } catch (e) {
        if (!cancelled) {
          setCountryError(e?.message || 'Harita dosyası okunamadı');
        }
      } finally {
        if (!cancelled) setCountryLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      el.innerHTML = '';
    };
  }, [viewMode]);

  useEffect(() => {
    const root = countryMountRef.current;
    if (!root || viewMode !== 'country' || countryLoading) return undefined;

    const onClick = (e) => {
      const province = e.target.closest('#features > g[id]');
      if (!province || !root.contains(province)) return;
      const id = province.getAttribute('id');
      const title = province.getAttribute('title') || id;
      const iso2 = province.getAttribute('iso2');
      if (!id || !iso2) return;
      setActiveProvince({ id, title, iso2 });
      setViewMode('districts');
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [viewMode, countryLoading]);

  useEffect(() => {
    if (
      viewMode !== 'country' ||
      countryLoading ||
      countryError ||
      !countryMountRef.current ||
      !mapLabelStateReady
    ) {
      return undefined;
    }

    const mount = countryMountRef.current;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const svg = mount.querySelector('svg');
        if (svg) addProvinceLabels(svg, { positionOverrides: mapLabelState.provinces });
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [viewMode, countryLoading, countryError, mapLabelStateReady, mapLabelState.provinces]);

  useEffect(() => {
    if (viewMode !== 'districts' || !activeCityPlate) {
      setCityDistricts([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchDistrictsByCity(activeCityPlate);
        const list = res?.veri?.ilceler || res?.veri?.districts || [];
        if (!cancelled) {
          setCityDistricts(Array.isArray(list) ? list : []);
        }
      } catch {
        if (!cancelled) {
          setCityDistricts([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewMode, activeCityPlate]);

  useEffect(() => {
    if (viewMode !== 'districts' || !activeCityPlate) {
      setCityAuthorizedDistrictIds([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchServiceAreasByCityPlate(activeCityPlate);
        const list = res?.veri?.yetkiAlanlari || [];
        const ids = [
          ...new Set(
            list
              .map((a) => Number(a.districtId))
              .filter((n) => Number.isFinite(n) && n >= 101 && n <= 8199)
          ),
        ];
        if (!cancelled) setCityAuthorizedDistrictIds(ids);
      } catch {
        if (!cancelled) setCityAuthorizedDistrictIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewMode, activeCityPlate]);

  // ——— İlçe (adm2 dilimi) ———
  useEffect(() => {
    if (viewMode !== 'districts' || !activeProvince?.iso2) return undefined;

    const el = districtMountRef.current;
    if (!el) return undefined;

    let cancelled = false;

    const load = async () => {
      flushSync(() => {
        setDistrictLoading(true);
      });
      setDistrictError('');
      try {
        const plate = platePrefixFromIso2(activeProvince.iso2);
        if (!plate) throw new Error('Bu il için plaka kodu okunamadı.');
        const markup = await fetchAdm2Markup();
        if (cancelled) return;
        const svg = createDistrictSliceSvg(markup, plate);
        if (!svg) throw new Error('Bu il için ilçe haritası bulunamadı.');
        el.innerHTML = '';
        el.appendChild(svg);
        svg.removeAttribute('style');
        svg.style.background = 'transparent';
        svg.style.display = 'block';
        svg.style.width = '100%';
        svg.style.height = 'auto';
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', `${activeProvince.title} ilçeleri`);
      } catch (e) {
        if (!cancelled) {
          setDistrictError(e?.message || 'İlçe haritası yüklenemedi');
        }
      } finally {
        if (!cancelled) setDistrictLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      el.innerHTML = '';
    };
  }, [viewMode, activeProvince?.id, activeProvince?.iso2, activeProvince?.title]);

  useEffect(() => {
    if (
      viewMode !== 'districts' ||
      districtLoading ||
      districtError ||
      !districtMountRef.current ||
      !mapLabelStateReady
    ) {
      return undefined;
    }

    const mount = districtMountRef.current;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const svg = mount.querySelector('svg');
        if (svg) {
          addDistrictLabels(svg, {
            positionOverrides: mapLabelState.districts,
            globalFontScale: mapLabelState.districtGlobalFontScale ?? 1,
          });
          requestAnimationFrame(() => fitDistrictSliceViewBox(svg));
        }
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [
    viewMode,
    districtLoading,
    districtError,
    activeProvince?.id,
    mapLabelStateReady,
    mapLabelState.districts,
    mapLabelState.districtGlobalFontScale,
  ]);

  useEffect(() => {
    const mount = districtMountRef.current;
    if (!mount || viewMode !== 'districts' || districtLoading || districtError) return undefined;

    const svg = mount.querySelector('svg');
    const features = svg?.querySelector('#features');
    if (!features) return undefined;

    const groups = features.querySelectorAll(':scope > g[id]');
    const auth = new Set(cityAuthorizedDistrictIds);
    const list = cityDistricts;
    const plate = activeCityPlate;

    groups.forEach((g) => {
      g.classList.remove(DISTRICT_AUTHORIZED_CLASS);
      if (
        !plate ||
        auth.size === 0 ||
        !Array.isArray(list) ||
        list.length === 0
      ) {
        return;
      }
      const dbId = svgDistrictGroupToDbDistrictId(g, plate, list);
      if (dbId != null && auth.has(dbId)) {
        g.classList.add(DISTRICT_AUTHORIZED_CLASS);
      }
    });

    return undefined;
  }, [
    viewMode,
    districtLoading,
    districtError,
    activeProvince?.id,
    cityDistricts,
    activeCityPlate,
    cityAuthorizedDistrictIds,
  ]);

  const closeDistrictModal = useCallback(() => {
    setDistrictModal(null);
  }, []);

  useEffect(() => {
    if (!districtModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDistrictModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [districtModal, closeDistrictModal]);

  useEffect(() => {
    const root = districtMountRef.current;
    if (!root || viewMode !== 'districts' || districtLoading || districtError || !activeCityPlate) return undefined;

    const onDistrictClick = (e) => {
      const g = e.target.closest('#features > g[id]');
      if (!g || !root.contains(g)) return;
      const list = cityDistricts;
      if (!Array.isArray(list) || list.length === 0) {
        if (window.showNotification) {
          window.showNotification('warning', 'İlçe listesi henüz yüklenmedi veya alınamadı. Sayfayı yenileyin.');
        }
        return;
      }
      const dbId = svgDistrictGroupToDbDistrictId(g, activeCityPlate, list);
      if (!dbId) {
        if (window.showNotification) {
          window.showNotification(
            'warning',
            'Bu harita ilçesi veritabanındaki ilçe adıyla eşleşmedi. Yöneticiye bildirin.'
          );
        }
        return;
      }
      const label = (g.getAttribute('title') || '').trim() || g.getAttribute('id') || '';
      setDistrictModal({ districtId: dbId, label });
    };

    root.addEventListener('click', onDistrictClick);
    return () => root.removeEventListener('click', onDistrictClick);
  }, [
    viewMode,
    districtLoading,
    districtError,
    activeCityPlate,
    cityDistricts,
    activeProvince?.id,
  ]);

  const handleBackToCountry = () => {
    setViewMode('country');
    setActiveProvince(null);
    setDistrictError('');
    closeDistrictModal();
    setCityDistricts([]);
  };

  const busy = viewMode === 'country' ? countryLoading : districtLoading;

  return (
    <main className="main-content tech-services-map-page">
      <header className="tech-services-map-page__header">
        <div>
          <h1>Teknik Servisler</h1>
          <p className="tech-services-map-page__lead">
            {viewMode === 'country' ? (
              <>Türkiye il haritasından bir ile tıklayarak ilçe sınırlarına geçin.</>
            ) : (
              <>
                <strong>{activeProvince?.title}</strong> ilçeleri —{' '}
                <span className="tech-services-map-page__lead-legend">
                  açık turuncu
                </span>{' '}
                alanlarda kayıtlı yetkili servis vardır; ilçeye tıklayınca detay açılır. Tüm Türkiye için Geri.
              </>
            )}
          </p>
        </div>
        {viewMode === 'districts' && activeProvince && (
          <div className="tech-services-map-page__header-actions">
            <button type="button" className="tech-services-map-page__back-btn" onClick={handleBackToCountry}>
              <i className="fas fa-arrow-left" aria-hidden="true" />
              Tüm Türkiye
            </button>
          </div>
        )}
      </header>

      <section className="tech-services-map-page__card" aria-busy={busy}>
        {viewMode === 'country' && countryLoading && (
          <div className="tech-services-map-page__loading">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span>Harita yükleniyor…</span>
          </div>
        )}
        {viewMode === 'country' && countryError && (
          <div className="tech-services-map-page__error" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
            <span>{countryError}</span>
          </div>
        )}
        {viewMode === 'districts' && districtLoading && (
          <div className="tech-services-map-page__loading">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span>İlçe haritası yükleniyor…</span>
          </div>
        )}
        {viewMode === 'districts' && districtError && (
          <div className="tech-services-map-page__error" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
            <span>{districtError}</span>
          </div>
        )}

        {viewMode === 'country' && (
          <MapZoomableFrame
            resetKey={`svc-${viewMode}-${activeProvince?.id ?? 'ülke'}`}
            hideToolbar={countryLoading || !!countryError}
          >
            <div
              ref={countryMountRef}
              className="tech-services-map-page__svg-root turkey-map-svg-root"
              hidden={!!countryError || countryLoading}
            />
          </MapZoomableFrame>
        )}
        {viewMode === 'districts' && (
          <MapZoomableFrame
            resetKey={`svc-${viewMode}-${activeProvince?.id ?? ''}`}
            hideToolbar={districtLoading || !!districtError}
          >
            <div
              ref={districtMountRef}
              className="tech-services-map-page__svg-root turkey-map-svg-root"
              hidden={!!districtError || districtLoading}
            />
          </MapZoomableFrame>
        )}
      </section>

      <TechServicesMapDistrictModal
        open={Boolean(districtModal)}
        onClose={closeDistrictModal}
        districtId={districtModal?.districtId ?? null}
        districtLabel={districtModal?.label || ''}
        mainCategories={mainProductCategories}
      />
    </main>
  );
};

export default TechServicesMapPage;
